import React, { useState, useEffect } from 'react';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import {
  User, Mail, Key, Edit, Save, X, Shield,
  AlertCircle, CheckCircle, Gamepad, Award, User2
} from 'lucide-react';

import './styles/account-info.css';

const client = generateClient();

const AccountInfo = ({ user }) => {
  const [userData, setUserData] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [profileStats, setProfileStats] = useState({
    characters: 0,
    games: 0, 
    achievements: 0,
    friends: 0
  });

  // Fetch user info on component mount
  useEffect(() => {
    fetchUserData();
    fetchUserGroups();
    fetchUserStats();
  }, [user]);

  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      // Get current user data from Cognito
      const authUser = await getCurrentUser();
      
      // Try to get extended profile from our API
      try {
        // Check if we have extended profile in our database
        const response = await client.graphql({
          query: `
          query GetUserProfile($id: String!) {
            getUserProfile(id: $id) {
              id
              displayName
              bio
              avatarUrl
              createdAt
              updatedAt
            }
          }
        `,
          variables: { id: authUser.userId }
        });
        
        const profile = response.data.getUserProfile;
        if (profile) {
          setDisplayName(profile.displayName || authUser.username);
          setBio(profile.bio || '');
          setUserData({
            ...authUser,
            ...profile
          });
        } else {
          // No extended profile yet, use basic Cognito data
          setDisplayName(authUser.username);
          setUserData(authUser);
        }
      } catch (error) {
        console.log('No extended profile found, using basic user data');
        setDisplayName(authUser.username);
        setUserData(authUser);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Failed to load user profile information');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserGroups = async () => {
    try {
      const session = await fetchAuthSession();
      const groups = session.tokens.accessToken.payload['cognito:groups'] || [];
      setUserGroups(groups);
    } catch (error) {
      console.error('Error fetching user groups:', error);
    }
  };

  const fetchUserStats = async () => {
    try {
      // Get character count for this user
      const charResponse = await client.graphql({
        query: `
        query ListCharacters($owner: String!) {
          listCharacters(filter: {owner: {eq: $owner}}) {
            items {
              id
            }
          }
        }
      `,
        variables: { owner: user.userId }
      });

      const characterCount = charResponse.data.listCharacters.items.length;

      // Fetch other stats from our database
      const statsResponse = await client.graphql({
        query: `
        query GetUserStats($userId: ID!) {
          getGameStates(userId: $userId) {
            items {
              id
            }
          }
          getUserAchievements(userId: $userId) {
            items {
              id
            }
          }
          getFriends(userId: $userId) {
            items {
              id
            }
          }
        }
      `,
        variables: { userId: user.userId }
      });
      
      // Values might be null if no records exist
      const gameCount = statsResponse.data.getGameStates?.items.length || 0;
      const achievementCount = statsResponse.data.getUserAchievements?.items.length || 0;
      const friendCount = statsResponse.data.getFriends?.items.length || 0;
      
      setProfileStats({
        characters: characterCount,
        games: gameCount,
        achievements: achievementCount,
        friends: friendCount
      });
    } catch (error) {
      console.error('Error fetching user stats:', error);
      // Set default stats on error
      setProfileStats({
        characters: 0,
        games: 0,
        achievements: 0,
        friends: 0
      });
    }
  };

  const handleUpdateProfile = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Create or update the extended profile
      const response = await client.graphql({
        query: `
        mutation UpdateUserProfile($input: UpdateUserProfileInput!) {
          updateUserProfile(input: $input) {
            id
            displayName
            bio
            avatarUrl
            updatedAt
          }
        }
      `,
        variables: {
          input: {
            id: userData.userId,
            displayName,
            bio
          }
        }
      });
      
      const updatedProfile = response.data.updateUserProfile;
      setUserData({
        ...userData,
        ...updatedProfile
      });
      
      setSuccess('Profile updated successfully');
      setIsEditing(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset form values to current user data
    setDisplayName(userData.displayName || userData.username);
    setBio(userData.bio || '');
    setIsEditing(false);
  };

  if (isLoading && !userData) {
    return (
      <div className="account-container">
        <div className="account-loading">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-container">
      <h1 className="account-title">
        <User size={24} />
        My Account
      </h1>

      {error && (
        <div className="account-alert error">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {success && (
        <div className="account-alert success">
          <CheckCircle size={18} />
          {success}
        </div>
      )}

      <div className="account-content">
        <div className="account-section account-profile">
          <div className="account-avatar">
            <div className="avatar-placeholder">
              <User2 size={64} />
            </div>
          </div>
          
          <div className="account-details">
            {isEditing ? (
              <div className="account-edit-form">
                <div className="form-group">
                  <label>Display Name</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    maxLength={30}
                  />
                </div>
                
                <div className="form-group">
                  <label>Bio</label>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    maxLength={250}
                    rows={4}
                    placeholder="Tell us about yourself..."
                  />
                  <span className="character-count">{bio.length}/250</span>
                </div>
                
                <div className="form-actions">
                  <button 
                    className="btn btn-secondary" 
                    onClick={handleCancelEdit}
                    disabled={isLoading}
                  >
                    <X size={16} /> Cancel
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={handleUpdateProfile}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : <><Save size={16} /> Save</>}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="account-header">
                  <h2 className="account-name">{displayName || userData.username}</h2>
                  <button 
                    className="btn btn-edit" 
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit size={16} /> Edit Profile
                  </button>
                </div>
                
                {userGroups.length > 0 && (
                  <div className="account-roles">
                    {userGroups.map(group => (
                      <span key={group} className={`role role-${group.toLowerCase()}`}>
                        <Shield size={12} /> {group}
                      </span>
                    ))}
                  </div>
                )}
                
                {bio && (
                  <p className="account-bio">{bio}</p>
                )}
                
                <div className="account-username">
                  <span className="label">Username:</span> {userData.username}
                </div>
                
                <div className="account-email">
                  <Mail size={16} /> {userData.attributes?.email || 'Email not available'}
                </div>
                
                <div className="account-joined">
                  <span className="label">Joined:</span> {new Date(userData.createdAt || Date.now()).toLocaleDateString()}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="account-section">
          <h3 className="section-title">Activity Stats</h3>
          <div className="stats-grid">
            <div className="stat-box">
              <Gamepad className="stat-icon" />
              <div className="stat-value">{profileStats.games}</div>
              <div className="stat-label">Games Played</div>
            </div>
            
            <div className="stat-box">
              <User className="stat-icon" />
              <div className="stat-value">{profileStats.characters}</div>
              <div className="stat-label">Characters</div>
            </div>
            
            <div className="stat-box">
              <Award className="stat-icon" />
              <div className="stat-value">{profileStats.achievements}</div>
              <div className="stat-label">Achievements</div>
            </div>
            
            <div className="stat-box">
              <User2 className="stat-icon" />
              <div className="stat-value">{profileStats.friends}</div>
              <div className="stat-label">Friends</div>
            </div>
          </div>
        </div>

        <div className="account-section">
          <h3 className="section-title">Account Security</h3>
          <div className="security-options">
            <button className="btn btn-secondary">
              <Mail size={16} /> Change Email
            </button>
            <button className="btn btn-secondary">
              <Key size={16} /> Change Password
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountInfo;